import { useQuery } from '@apollo/client';
import { useCallback, useContext, useMemo } from 'react';
import { Attachment } from 'sr-types/lib/production/v1/graphql';
import type { EntitySecurity } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../constants/constants';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { buildStatusReportsFilter } from './reportContextFilter';
import { searchClient, slimSearchQuery } from './slimQuery';

export type StatusReportAttachment = Attachment & { labels?: string[] };

type DocumentHit = {
    id: string;
    headline?: string;
    size?: number;
    contentType?: string;
    createdOn?: string;
    tags?: string[];
    security?: EntitySecurity;
};

type UseStatusReportsArgs = {
    contextTerm?: string;
    contextEntities?: Array<{ type?: string; id?: string }>;
};

export const useStatusReports = (args?: UseStatusReportsArgs) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const { contextTerm, contextEntities } = args || {};
    const filter = useMemo(
        () => buildStatusReportsFilter(contextTerm, contextEntities),
        [contextTerm, contextEntities]
    );

    const { data, loading, refetch } = useQuery(slimSearchQuery(ENTITY_CONSTANTS.document, true), {
        client: searchClient,
        variables: {
            entity: ENTITY_CONSTANTS.document,
            filter,
            includeAllActions: true,
            sortBy: 'createdOn_DESC',
            page: { from: 0, size: 200 }
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange: true
    });

    const mapHitToAttachment = useCallback(
        (d: DocumentHit): StatusReportAttachment => ({
            entityReference: { id: d.id },
            name: d?.headline,
            size: d?.size,
            type: d?.contentType,
            createdOn: d?.createdOn,
            tags: Array.isArray(d?.tags) ? d.tags : [],
            labels: Array.isArray(d?.tags) ? d.tags : [], // shown as label chips on attachment cards
            security: d.security
        }),
        []
    );

    const attachments: StatusReportAttachment[] = useMemo(() => {
        const docs: DocumentHit[] = data?.search?.hits?.items || [];
        return docs.map(mapHitToAttachment);
    }, [data?.search?.hits?.items, mapHitToAttachment]);

    return { attachments, loading, refetch };
};
